import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Table, Btn, Input, Select, Modal, Badge } from '../../components/UI';
import { useMsal } from '@azure/msal-react';
import { api, getUser } from '../../utils/api';
import { addToOutlook, downloadIcs, hasDate, wasAdded, formatRange } from '../../utils/outlookCalendar';

const SUBNAV = [
  { href: '/news/events', label: 'Events — sign up' },
  { href: '/news/latest', label: 'News & Calendar' },
];

// Event types (WI-X1) and how they group on the page.
const KIND_GROUPS = [
  ['Expeditions', 'expedition'],
  ['Sign up for a Hike', 'hike'],
  ['Roundtables', 'roundtable'],
  ['Other', 'other'],
];
// Kreiranje/zakazivanje evenata — samo Event Organizer podrola (+ specifični
// organizatori) + SUPERADMIN. Bez ADM/SH/BP/legacy.
const isEventAdmin = (u) =>
  (u?.roles || []).includes('SUPERADMIN') ||
  (u?.funcs || []).some(f => ['Event Organizer', 'Expedition Organization', 'Hike Organizer'].includes(f));
// Hike Organizer — odobrava Call a Hike zahteve.
const isHikeOrganizer = (u) =>
  (u?.roles || []).includes('SUPERADMIN') ||
  (u?.funcs || []).some(f => ['Event Organizer', 'Hike Organizer'].includes(f));

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

// "When" shown to people: the real date/time if the event has one, else the free text.
const whenOf = (e) => (e.startsAt ? formatRange(e.startsAt, e.endsAt) : (e.when || 'tba'));

// Add an event to the person's own Outlook calendar (they consent once, in Microsoft's prompt).
// On failure (no consent, blocked popup…) it offers the .ics file instead.
function AddToOutlook({ event, size = 'sm', onDone }) {
  const { instance } = useMsal();
  const [state, setState] = useState(() => (wasAdded(event.id) ? 'done' : 'idle')); // idle | busy | done | error
  const [err, setErr] = useState('');
  if (!hasDate(event)) return null;
  async function add() {
    setState('busy'); setErr('');
    try { await addToOutlook(instance, event); setState('done'); onDone && onDone(); }
    catch (e) { setErr(e.message || 'Could not reach Outlook'); setState('error'); }
  }
  const link = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.76rem', color: 'var(--ink-soft)', textDecoration: 'underline' };
  if (state === 'done') {
    return <span style={{ fontSize: '0.76rem', color: 'var(--ink-soft)' }}>📅 In your Outlook</span>;
  }
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
      <Btn size={size} variant="ghost" loading={state === 'busy'} onClick={add} style={{ padding: size === 'sm' ? '3px 0' : undefined }}>📅 Add to my Outlook</Btn>
      {state === 'error' && (
        <span style={{ fontSize: '0.72rem', color: 'var(--danger)', maxWidth: 220 }}>
          {err}. <button style={link} onClick={() => downloadIcs(event)}>Download .ics instead</button>
        </span>
      )}
    </span>
  );
}

// Right after signing up: ask whether to put the event in Outlook (only events with a date).
function OutlookPrompt({ event, onClose }) {
  const { instance } = useMsal();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  async function add() {
    setBusy(true); setErr('');
    try { await addToOutlook(instance, event); setDone(true); }
    catch (e) { setErr(e.message || 'Could not reach Outlook'); }
    finally { setBusy(false); }
  }
  return (
    <Modal open onClose={onClose} title="You're signed up ✓">
      {done ? (
        <>
          <p style={{ fontSize: '0.9rem', marginTop: 0 }}>“{event.title}” is now in your Outlook calendar, with a reminder 30 minutes before.</p>
          <Btn onClick={onClose}>Done</Btn>
        </>
      ) : (
        <>
          <p style={{ fontSize: '0.9rem', marginTop: 0 }}>Add <b>{event.title}</b> ({whenOf(event)}) to your Outlook calendar?</p>
          <p style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>The first time, Microsoft asks you to allow Basecamp to add events to your calendar. Basecamp only adds the events you choose.</p>
          {err && <div style={{ marginBottom: 12 }}><Alert type="error">{err} — you can <button onClick={() => downloadIcs(event)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', color: 'inherit', font: 'inherit' }}>download an .ics file</button> instead.</Alert></div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={add} loading={busy}>📅 Add to Outlook</Btn>
            <Btn variant="ghost" onClick={onClose}>Not now</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Events ─────────────────────────────────────────────────────────────────
function Events() {
  const [state, setState] = useState({ loading: true, error: null, events: [] });
  const [callForm, setCallForm] = useState({ title: '', about: '', online: false });
  const [callBusy, setCallBusy] = useState(false);
  const [callSent, setCallSent] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [outlookEvent, setOutlookEvent] = useState(null);
  const user = getUser();
  const canCall = (user?.roles || []).some(r => ['IP', 'BP', 'ADM', 'SH', 'PM'].includes(r));
  const admin = isEventAdmin(user);
  const hikeOrganizer = isHikeOrganizer(user);
  const setCall = (k, v) => setCallForm(f => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    api.getEvents()
      .then(d => setState({ loading: false, error: null, events: d.events }))
      .catch(e => setState({ loading: false, error: e.message, events: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function signup(id) {
    const ev = state.events.find(x => x.id === id);
    try {
      await api.toggleSignup(id);
      load();
      // Signing up (not withdrawing) for a dated event → offer Outlook.
      if (ev && !ev.signedUp && hasDate(ev) && !wasAdded(ev.id)) setOutlookEvent(ev);
    } catch (e) { alert(e.message); }
  }
  async function approveHike(id) { try { await api.updateEvent(id, { status: 'scheduled', organiserSelf: true }); load(); } catch (e) { alert(e.message); } }
  async function declineHike(id) { try { await api.updateEvent(id, { status: 'cancelled' }); load(); } catch (e) { alert(e.message); } }

  async function callHike(e) {
    e.preventDefault();
    if (!callForm.title.trim()) return;
    setCallBusy(true);
    try {
      await api.createEvent({
        title: callForm.title.trim(),
        about: callForm.about.trim() || null,
        kind: 'hike',
        format: callForm.online ? 'online' : 'onsite',
      });
      setCallForm({ title: '', about: '', online: false });
      setCallSent(true);
      load();
    } catch (err) { alert(err.message); } finally { setCallBusy(false); }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  const canSignUp = (e) => e.status === 'scheduled';
  const pendingHikes = state.events.filter(e => e.kind === 'hike' && e.status === 'requested');

  return (
    <>
      <Alert type="info">All upcoming events with everything you need — and one-click sign-up. Click an event for details.</Alert>

      {admin && <ScheduleEventForm onCreated={load} />}

      {/* Call for a Hike — headline, description, online? → ide Hike Organizer-u na odobrenje */}
      {canCall && (
        <Card style={{ padding: '18px 22px', marginTop: 18 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 6 }}>Call for a Hike</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginBottom: 12 }}>
            Propose a Hike — it goes to a Hike Organizer for approval. Once approved it appears below for sign-up.
          </p>
          <form onSubmit={callHike} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Input placeholder="Headline" value={callForm.title} onChange={e => setCall('title', e.target.value)} />
            <textarea
              placeholder="Description — what the Hike is about"
              value={callForm.about}
              onChange={e => setCall('about', e.target.value)}
              style={{ padding: '10px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', background: 'var(--canvas-white)', color: 'var(--ink)', fontSize: '0.9rem', outline: 'none', width: '100%', fontFamily: 'var(--font-body)', resize: 'vertical', minHeight: 70, boxSizing: 'border-box' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem', color: 'var(--ink-soft)' }}>
              <input type="checkbox" checked={callForm.online} onChange={e => setCall('online', e.target.checked)} />
              Online
            </label>
            <Btn type="submit" loading={callBusy} style={{ alignSelf: 'flex-start' }}>Call for a Hike</Btn>
          </form>
          {callSent && <div style={{ marginTop: 12 }}><Alert type="success">Your Hike request has been sent for approval.</Alert></div>}
        </Card>
      )}

      {/* Hike Organizer — red zahteva za odobravanje */}
      {hikeOrganizer && pendingHikes.length > 0 && (
        <Card style={{ padding: '18px 22px', marginTop: 18 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>Hike requests — pending approval</h3>
          <Table
            headers={['Headline', 'Description', 'Format', 'Requested by', '']}
            rows={pendingHikes.map(h => [
              <b>{h.title}</b>,
              <span style={{ fontSize: '0.82rem', color: 'var(--ink-soft)' }}>{h.about || '—'}</span>,
              h.format || '—',
              h.initiatedBy || '—',
              <span style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" onClick={() => approveHike(h.id)}>Approve</Btn>
                <Btn size="sm" variant="outline" onClick={() => declineHike(h.id)}>Decline</Btn>
              </span>,
            ])}
          />
        </Card>
      )}

      {KIND_GROUPS.map(([label, kind]) => {
        const list = state.events.filter(e => e.kind === kind && e.status === 'scheduled');
        // Expeditions & Hikes always show; Roundtables/Other only when they have events.
        if (!list.length && !['expedition', 'hike'].includes(kind)) return null;
        return (
          <Card key={kind} style={{ padding: '18px 22px', marginTop: 18 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>{label}</h3>
            <Table
              headers={['Event', 'When · where', 'Organiser', 'About', 'Sign-up']}
              rows={list.map(e => [
                <button onClick={() => setOpenId(e.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--ink)', textAlign: 'left', textDecoration: 'underline', fontFamily: 'var(--font-body)', fontSize: '0.9rem' }}>{e.title}</button>,
                `${whenOf(e)}${e.location ? ` · ${e.location}` : e.format ? ` · ${e.format}` : ''}`,
                e.organiser || 'to be assigned',
                <span style={{ fontSize: '0.82rem' }}>
                  {e.about}
                  <br />
                  <span style={{ color: 'var(--ink-faint)' }}>
                    {e.capacity ? `${e.capacity} places · ` : ''}{e.signups} signed up
                  </span>
                </span>,
                canSignUp(e)
                  ? (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                      <Btn size="sm" variant={e.signedUp ? 'primary' : 'outline'} onClick={() => signup(e.id)}>{e.signedUp ? 'Signed up ✓' : 'Sign up'}</Btn>
                      {e.signedUp && <AddToOutlook key={`o-${e.id}`} event={e} />}
                    </span>
                  )
                  : <span style={{ color: 'var(--ink-faint)', fontSize: '0.8rem' }}>soon</span>,
              ])}
              emptyMessage="No events scheduled"
            />
          </Card>
        );
      })}

      {openId != null && (
        <EventDetailModal id={openId} onClose={() => setOpenId(null)} onChanged={load} />
      )}
      {outlookEvent && <OutlookPrompt event={outlookEvent} onClose={() => setOutlookEvent(null)} />}
    </>
  );
}

// Administration (ADM / Hike administrator) schedules an event of any type.
// A real start/end lets people add the event to their Outlook calendar.
const dtLabel = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: 'var(--ink-faint)', fontWeight: 600 };
function ScheduleEventForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const blank = { title: '', kind: 'hike', start: '', end: '', whenText: '', location: '', format: 'onsite', capacity: '', about: '' };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!f.title.trim() || (!f.start && !f.whenText.trim())) return;
    if (f.start && f.end && new Date(f.end) <= new Date(f.start)) { alert('The event must end after it starts.'); return; }
    setBusy(true);
    try {
      // datetime-local is the organiser's local time → send as UTC ISO.
      const startsAt = f.start ? new Date(f.start).toISOString() : null;
      const endsAt = f.end ? new Date(f.end).toISOString() : null;
      await api.createEvent({
        title: f.title.trim(), kind: f.kind,
        whenText: f.whenText.trim() || (startsAt ? formatRange(startsAt, endsAt) : ''),
        startsAt, endsAt,
        location: f.location.trim() || null, format: f.format,
        capacity: f.capacity ? Number(f.capacity) : null, about: f.about.trim() || null,
      });
      setF(blank);
      setOpen(false); onCreated();
    } catch (err) { alert(err.message); } finally { setBusy(false); }
  }

  return (
    <Card style={{ padding: '18px 22px', marginTop: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}>Schedule an event</h3>
        <Btn size="sm" variant={open ? 'outline' : 'primary'} onClick={() => setOpen(o => !o)}>{open ? 'Cancel' : '+ New event'}</Btn>
      </div>
      {open && (
        <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
          <Input placeholder="Event title" value={f.title} onChange={e => set('title', e.target.value)} />
          <Select value={f.kind} onChange={e => set('kind', e.target.value)}>
            <option value="expedition">Expedition</option>
            <option value="hike">Hike</option>
            <option value="roundtable">Roundtable</option>
            <option value="other">Other</option>
          </Select>
          <label style={dtLabel}>Starts
            <Input type="datetime-local" value={f.start} onChange={e => set('start', e.target.value)} />
          </label>
          <label style={dtLabel}>Ends
            <Input type="datetime-local" value={f.end} min={f.start || undefined} onChange={e => set('end', e.target.value)} />
          </label>
          <Input placeholder={f.start ? 'When — optional label (auto from the date)' : 'When (if there is no exact date yet)'} value={f.whenText} onChange={e => set('whenText', e.target.value)} />
          <Input placeholder="Place / location" value={f.location} onChange={e => set('location', e.target.value)} />
          <Select value={f.format} onChange={e => set('format', e.target.value)}>
            <option value="onsite">Onsite</option>
            <option value="online">Online</option>
          </Select>
          <Input type="number" min="0" placeholder="Capacity (optional)" value={f.capacity} onChange={e => set('capacity', e.target.value)} />
          <Input placeholder="About (optional)" value={f.about} onChange={e => set('about', e.target.value)} style={{ gridColumn: '1 / -1' }} />
          <Btn type="submit" loading={busy} style={{ justifySelf: 'start' }}>Schedule</Btn>
        </form>
      )}
    </Card>
  );
}

// Event detail — full info, sign-up, and (for the organiser/ADM) the attendee list.
function EventDetailModal({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.getEvent(id).then(setData).catch(e => setError(e.message));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function signup() {
    setBusy(true);
    try { await api.toggleSignup(id); await load(); onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  const e = data?.event;
  return (
    <Modal open onClose={onClose} title={e ? e.title : 'Event'}>
      {error && <Alert type="error">{error}</Alert>}
      {!data ? <Loading /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge status="default">{e.kind}</Badge>
            <Badge status={e.status === 'scheduled' ? 'active' : 'default'}>{e.status}</Badge>
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
            <div><b>When:</b> {whenOf(e)}{e.format ? ` · ${e.format}` : ''}</div>
            {e.location && <div><b>Where:</b> {e.location}</div>}
            <div><b>Organiser:</b> {e.organiser || 'to be assigned'}</div>
            <div><b>Signed up:</b> {e.signups}{e.capacity ? ` / ${e.capacity} places` : ''}</div>
          </div>
          {e.about && <p style={{ fontSize: '0.88rem', lineHeight: 1.55, margin: 0 }}>{e.about}</p>}

          {e.status === 'scheduled' && (
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <Btn variant={e.signedUp ? 'primary' : 'outline'} loading={busy} onClick={signup}>
                {e.signedUp ? 'Signed up ✓ — withdraw' : 'Sign up'}
              </Btn>
              {e.signedUp && <AddToOutlook event={e} />}
            </div>
          )}

          {data.canManage && (
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-faint)', margin: '6px 0 8px' }}>
                Attendees ({data.attendees.length})
              </div>
              {data.attendees.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                  {data.attendees.map(a => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span>{a.name || `#${a.id}`}</span>
                      <span style={{ color: 'var(--ink-faint)' }}>{a.roles.join(', ')}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: '0.85rem', color: 'var(--ink-faint)' }}>No sign-ups yet.</p>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── News & Calendar ───────────────────────────────────────────────────────
function News() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [form, setForm] = useState({ title: '', body: '' });
  const user = getUser();
  const canPost = (user?.roles || []).includes('SUPERADMIN') ||
    (user?.funcs || []).includes('Event Organizer');

  const load = useCallback(() => {
    api.getNews()
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function post(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    try { await api.createNews(form); setForm({ title: '', body: '' }); load(); }
    catch (err) { alert(err.message); }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  return (
    <div className="grid-2col" style={{ gap: 24, alignItems: 'start' }}>
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 12 }}>News</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {state.data.news.map(n => (
            <Card key={n.id} style={{ padding: '16px 20px' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{n.title}</div>
              {n.body && <div style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', lineHeight: 1.55 }}>{n.body}</div>}
              <div style={{ fontSize: '0.72rem', color: 'var(--ink-faint)', marginTop: 8 }}>
                {n.author || 'HB GmbH'} · {new Date(n.createdAt).toLocaleDateString()}
              </div>
            </Card>
          ))}
          {state.data.news.length === 0 && <p style={{ color: 'var(--ink-faint)', fontSize: '0.85rem' }}>No news yet.</p>}
        </div>
        {canPost && (
          <form onSubmit={post} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            <Input placeholder="News title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            <Input placeholder="Body (optional)" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
            <Btn type="submit" style={{ alignSelf: 'flex-start' }}>Publish</Btn>
          </form>
        )}
      </div>
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 12 }}>Calendar</h3>
        <Card style={{ padding: '14px 18px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginBottom: 10 }}>
            One calendar fed by Expeditions, Hikes and certification windows.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {state.data.calendar.map(e => (
              <div key={e.id} style={{ display: 'flex', gap: 10, fontSize: '0.86rem', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-faint)', width: 76, flexShrink: 0 }}>
                  {e.kind}
                </span>
                <span style={{ flex: 1 }}>{e.title}</span>
                <span style={{ color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{e.when || 'tba'}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default function NewsPages() {
  const { sub } = useParams();
  if (!sub) return <Navigate to="/news/events" replace />;

  let body;
  if (sub === 'events') body = <Events />;
  else if (sub === 'latest') body = <News />;
  else return <Navigate to="/news/events" replace />;

  return (
    <BasecampLayout>
      <PageHeader title="News & Events" subtitle="Community events, announcements & calendar" />
      <AreaLayout items={SUBNAV}>{body}</AreaLayout>
    </BasecampLayout>
  );
}